import React, { useEffect, useState } from "react";
import { QuillLogo } from "../components/QuillLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SAMPLE_URL = `${API}/book/sample`;

const INK = "#1C1A17";
const PAPER = "#F4F1EA";
const OXBLOOD = "#7A2E2E";
const OXBLOOD_WARM = "#B0573F";
const PEWTER = "#8A8F8A";
const SLATE = "#6E6A5E";

const body = { fontFamily: "Lato, system-ui, sans-serif" };

const OxbloodLink = ({ href, children, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    data-testid={testid}
    className="inline-block mt-6 font-serif transition-colors"
    style={{ backgroundColor: OXBLOOD, color: PAPER, textDecoration: "none", padding: "13px 30px", fontSize: "14px", letterSpacing: "0.12em" }}
    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = OXBLOOD_WARM)}
    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = OXBLOOD)}
  >
    {children}
  </a>
);

const ResendForm = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done
  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      await fetch(`${API}/book/ebook/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch { /* ignore */ }
    setState("done");
  };
  if (state === "done") {
    return (
      <p className="mt-6 text-[14px] font-serif italic" style={{ color: OXBLOOD_WARM, maxWidth: "48ch", marginInline: "auto" }} data-testid="thankyou-resend-done">
        If that email has a purchase, a fresh download link is on its way. Please check your inbox, and spam or promotions too.
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch justify-center max-w-[460px] mx-auto" data-testid="thankyou-resend-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your purchase email"
        data-testid="thankyou-resend-email"
        className="bg-transparent py-3 px-1 text-[15px] outline-none rounded-none flex-1"
        style={{ ...body, borderBottom: `1px solid ${PEWTER}`, color: PAPER }}
      />
      <button
        type="submit"
        disabled={state === "sending"}
        data-testid="thankyou-resend-submit"
        className="px-6 py-3 text-[12px] uppercase font-serif transition-colors disabled:opacity-60 whitespace-nowrap"
        style={{ letterSpacing: "0.16em", border: `1px solid ${OXBLOOD_WARM}`, color: OXBLOOD_WARM }}
      >
        {state === "sending" ? "Sending" : "Send a fresh link"}
      </button>
    </form>
  );
};

const BookThankYou = () => {
  const [status, setStatus] = useState("checking"); // checking | paid | pending | unknown
  const [order, setOrder] = useState(null);

  useEffect(() => {
    document.title = "Thank you · The Raven's Quill";
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get("order_id");
    if (!orderId) {
      setStatus("unknown");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const res = await fetch(`${API}/book/orders/${orderId}`);
        const data = await res.json();
        setOrder(data);
        if (data.status === "paid") {
          setStatus("paid");
          return;
        }
      } catch { /* keep polling */ }
      if (tries >= 30) {
        setStatus("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  const isEbook = order && order.product === "ebook";
  const isPre = order && order.preorder;
  const releaseDate = (order && order.release_date) || "1 December 2026";
  const ttl = (order && order.download_ttl_hours) || 72;

  const confirmedBody = isPre
    ? `Thank you. Your signed copy is reserved, and it will be despatched from ${releaseDate}.`
    : "Thank you. Your signed copy is reserved, and it will ship promptly.";

  const isCourier = order && (order.delivery_method === "courier_sa" || order.delivery_method === "courier_sadc");
  const deliveryAddress = order
    ? [order.address, order.suburb, order.city, order.province, order.country, order.postal_code].filter(Boolean).join(", ")
    : "";

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: INK, color: PAPER }} data-testid="book-thank-you-page">
      <div className="max-w-[580px] text-center py-20">
        <div className="flex justify-center">
          <QuillLogo height={80} variant="gold" />
        </div>
        <p className="mt-8 text-[11px] uppercase font-serif" style={{ letterSpacing: "0.28em", color: OXBLOOD_WARM }}>
          No One Is Coming To Save You
        </p>
        <hr className="border-0 mx-auto mt-6" style={{ width: 64, height: "1px", backgroundColor: OXBLOOD_WARM }} />

        {status === "checking" && (
          <>
            <h1 className="mt-10 font-serif italic" style={{ fontSize: "clamp(26px, 4.8vw, 38px)", color: PAPER }} data-testid="thankyou-checking">
              Confirming your order
            </h1>
            <p className="mt-6 text-[16px] leading-[1.8]" style={{ ...body, color: "#D8D3C6", maxWidth: "48ch", marginInline: "auto" }}>
              One moment, we are confirming your payment with PayFast. This usually takes only a few seconds. You do not need to do anything, this page will update on its own, and a confirmation email will follow.
            </p>
          </>
        )}

        {status === "paid" && isEbook && (
          <>
            <h1 className="mt-10 font-serif italic" style={{ fontSize: "clamp(28px, 5vw, 40px)", color: PAPER }} data-testid="thankyou-ebook-paid">
              Your ebook is ready
            </h1>
            <p className="mt-6 text-[16px] leading-[1.8]" style={{ ...body, color: "#D8D3C6", maxWidth: "52ch", marginInline: "auto" }}>
              Thank you. Your copy is ready to download now. A copy of this link is also on its way to the email you entered, so check your inbox, and your spam or promotions folder too.
            </p>
            {order.download_url && <OxbloodLink href={order.download_url} testid="thankyou-ebook-download">Download your ebook</OxbloodLink>}
            <p className="mt-6 text-[14px] leading-[1.8]" style={{ ...body, color: PEWTER, maxWidth: "52ch", marginInline: "auto" }}>
              This link is personal to you and expires in about {ttl} hours. If it expires, enter your purchase email below and I will send a fresh one. For anything else, reach me at{" "}
              <a href="mailto:info@theravensquill.co.za" style={{ color: OXBLOOD_WARM }}>info@theravensquill.co.za</a>.
            </p>
            <ResendForm />
          </>
        )}

        {status === "paid" && !isEbook && (
          <>
            <h1 className="mt-10 font-serif italic" style={{ fontSize: "clamp(28px, 5vw, 40px)", color: PAPER }} data-testid="thankyou-paid">
              {isPre ? "Your pre-order is confirmed" : "Your order is confirmed"}
            </h1>
            <p className="mt-6 text-[16px] leading-[1.8]" style={{ ...body, color: "#D8D3C6", maxWidth: "52ch", marginInline: "auto" }}>
              {confirmedBody}
            </p>
            <p className="mt-5 text-[15px] leading-[1.8]" style={{ ...body, color: "#C7C2B6", maxWidth: "52ch", marginInline: "auto" }}>
              A confirmation email is on its way to the address you entered on the order form. Please check your inbox, and your spam or promotions folder too, in case it lands there. If you do not see it within a few minutes, you can reach me at{" "}
              <a href="mailto:info@theravensquill.co.za" style={{ color: OXBLOOD_WARM }}>info@theravensquill.co.za</a>{" "}and I will sort it out.
            </p>
            {isCourier && deliveryAddress && (
              <div className="mt-8 mx-auto max-w-[44ch] text-left" data-testid="thankyou-delivery-address">
                <p className="text-[11px] uppercase font-serif" style={{ letterSpacing: "0.22em", color: OXBLOOD_WARM }}>Delivering to</p>
                <p className="mt-2 text-[15px] leading-[1.7] pl-4 py-2" style={{ ...body, color: PAPER, borderLeft: `3px solid ${OXBLOOD_WARM}` }}>
                  {deliveryAddress}
                </p>
                <p className="mt-2 text-[13px] leading-[1.7]" style={{ ...body, color: PEWTER }}>
                  If any part of this address is wrong, email me at{" "}
                  <a href="mailto:info@theravensquill.co.za?subject=Address%20correction%3A%20No%20One%20Is%20Coming%20To%20Save%20You" style={{ color: OXBLOOD_WARM }}>info@theravensquill.co.za</a>{" "}
                  and I will correct it before despatch.
                </p>
              </div>
            )}
            <div className="mt-12 pt-10" style={{ borderTop: `1px solid rgba(255,255,255,0.08)` }}>
              <p className="text-[15px] leading-[1.8]" style={{ ...body, color: PEWTER, maxWidth: "50ch", marginInline: "auto" }}>
                While you wait, here is the opening of the book to read now, the Introduction and the first chapter, in full.
              </p>
              <OxbloodLink href={SAMPLE_URL} testid="thankyou-preview-button">Download your preview chapter</OxbloodLink>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <h1 className="mt-10 font-serif italic" style={{ fontSize: "clamp(26px, 4.5vw, 36px)", color: PAPER }} data-testid="thankyou-pending">
              Confirming your order
            </h1>
            <p className="mt-6 text-[16px] leading-[1.8]" style={{ ...body, color: "#D8D3C6", maxWidth: "52ch", marginInline: "auto" }}>
              We are still confirming your payment with PayFast. This can occasionally take a few minutes. Your confirmation email will follow as soon as it clears. If anything seems amiss, please write to me at{" "}
              <a href="mailto:info@theravensquill.co.za" style={{ color: OXBLOOD_WARM }}>info@theravensquill.co.za</a>.
            </p>
            {isEbook ? (
              <div className="mt-8">
                <p className="text-[14px] leading-[1.8]" style={{ ...body, color: PEWTER, maxWidth: "50ch", marginInline: "auto" }}>
                  Once it clears, your download link appears here and in your inbox. Already paid? Enter your purchase email for your link.
                </p>
                <ResendForm />
              </div>
            ) : (
              <div className="mt-10">
                <p className="text-[15px] leading-[1.8]" style={{ ...body, color: PEWTER, maxWidth: "50ch", marginInline: "auto" }}>
                  In the meantime, here is the opening of the book to read now.
                </p>
                <OxbloodLink href={SAMPLE_URL} testid="thankyou-preview-button">Download your preview chapter</OxbloodLink>
              </div>
            )}
          </>
        )}

        {status === "unknown" && (
          <h1 className="mt-10 font-serif italic" style={{ fontSize: "clamp(26px, 4.5vw, 36px)", color: PAPER }} data-testid="thankyou-unknown">
            Thank you for visiting.
          </h1>
        )}

        <div className="mt-12">
          <a
            href="/no-one-is-coming"
            data-testid="thankyou-back-link"
            className="text-[11px] uppercase font-serif transition-colors"
            style={{ letterSpacing: "0.24em", color: PEWTER }}
            onMouseEnter={(e) => (e.currentTarget.style.color = OXBLOOD_WARM)}
            onMouseLeave={(e) => (e.currentTarget.style.color = PEWTER)}
          >
            Return to the book
          </a>
        </div>
        <p className="mt-10 text-[12px]" style={{ ...body, color: SLATE }}>
          The Raven&rsquo;s Quill &middot; Pretoria, South Africa
        </p>
      </div>
    </div>
  );
};

export default BookThankYou;
